import { useState, useCallback } from 'react';
import {
  Loader2,
  Plus,
  Search,
  TrendingUp,
  X,
  XCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { cn } from '../../lib/utils';
import type {
  AutocompleteResult,
  InterestOverTimeResult,
  RelatedQueriesResult,
} from '../../types';
import * as googleTrendsService from '../../services/google-trends.service';

interface GoogleTrendsPageProps {
  accessToken?: string;
  refreshToken?: string;
  attemptRefresh: () => Promise<boolean>;
  isAdmin: boolean;
}

const KEYWORD_COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6'];

const COUNTRY_OPTIONS = [
  { value: 'BR', label: 'Brasil (BR)' },
  { value: 'US', label: 'Estados Unidos (US)' },
  { value: 'PT', label: 'Portugal (PT)' },
];

const TIMEFRAME_OPTIONS = [
  { value: 'now 7-d', label: '7 dias' },
  { value: 'today 1-m', label: '30 dias' },
  { value: 'today 3-m', label: '90 dias' },
  { value: 'today 12-m', label: '12 meses' },
  { value: 'today 5-y', label: '5 anos' },
];

const LOCALE_BY_COUNTRY: Record<string, string> = {
  BR: 'pt-BR',
  US: 'en-US',
  PT: 'pt-PT',
};

export function GoogleTrendsPage({
  accessToken,
  attemptRefresh,
}: GoogleTrendsPageProps) {
  const [keyword, setKeyword] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [country, setCountry] = useState('BR');
  const [timeframe, setTimeframe] = useState('today 12-m');

  const [autocompleteResult, setAutocompleteResult] = useState<AutocompleteResult | null>(null);
  const [interestResult, setInterestResult] = useState<InterestOverTimeResult | null>(null);
  const [relatedResult, setRelatedResult] = useState<RelatedQueriesResult | null>(null);

  const [loadingAutocomplete, setLoadingAutocomplete] = useState(false);
  const [loadingInterest, setLoadingInterest] = useState(false);
  const [loadingRelated, setLoadingRelated] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const getToken = useCallback(async (): Promise<string | null> => {
    if (accessToken) return accessToken;
    const refreshed = await attemptRefresh();
    return refreshed ? accessToken ?? null : null;
  }, [accessToken, attemptRefresh]);

  const fetchAutocomplete = useCallback(
    async (kw: string) => {
      const token = await getToken();
      if (!token) return;
      setLoadingAutocomplete(true);
      try {
        const locale = LOCALE_BY_COUNTRY[country] ?? 'pt-BR';
        const result = await googleTrendsService.fetchAutocomplete(kw, locale, country, token);
        setAutocompleteResult(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao buscar sugestões');
      } finally {
        setLoadingAutocomplete(false);
      }
    },
    [getToken, country]
  );

  const fetchInterestOverTime = useCallback(
    async (kws: string[]) => {
      if (kws.length === 0) return;
      const token = await getToken();
      if (!token) return;
      setLoadingInterest(true);
      try {
        const result = await googleTrendsService.fetchInterestOverTime(kws, country, timeframe, token);
        setInterestResult(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao buscar interesse ao longo do tempo');
      } finally {
        setLoadingInterest(false);
      }
    },
    [getToken, country, timeframe]
  );

  const fetchRelatedQueries = useCallback(
    async (kw: string) => {
      const token = await getToken();
      if (!token) return;
      setLoadingRelated(true);
      try {
        const result = await googleTrendsService.fetchRelatedQueries(kw, country, timeframe, token);
        setRelatedResult(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erro ao buscar consultas relacionadas');
      } finally {
        setLoadingRelated(false);
      }
    },
    [getToken, country, timeframe]
  );

  const handleSearch = useCallback(async () => {
    if (!keyword.trim()) return;
    setError(null);

    const activeKeyword = keyword.trim();
    const kws = keywords.length === 0 ? [activeKeyword] : keywords;

    await Promise.all([
      fetchAutocomplete(activeKeyword),
      fetchInterestOverTime(kws),
      fetchRelatedQueries(activeKeyword),
    ]);
  }, [keyword, keywords, fetchAutocomplete, fetchInterestOverTime, fetchRelatedQueries]);

  const handleAddKeyword = useCallback(
    async (suggestion: string) => {
      if (keywords.includes(suggestion)) return;
      if (keywords.length >= 5) return;
      const newKeywords = [...keywords, suggestion];
      setKeywords(newKeywords);
      setError(null);
      await fetchInterestOverTime(newKeywords);
    },
    [keywords, fetchInterestOverTime]
  );

  const handleRemoveKeyword = useCallback(
    async (kw: string) => {
      const newKeywords = keywords.filter((k) => k !== kw);
      setKeywords(newKeywords);
      if (newKeywords.length > 0) {
        setError(null);
        await fetchInterestOverTime(newKeywords);
      } else {
        setInterestResult(null);
      }
    },
    [keywords, fetchInterestOverTime]
  );

  const handleClear = useCallback(() => {
    setKeyword('');
    setKeywords([]);
    setAutocompleteResult(null);
    setInterestResult(null);
    setRelatedResult(null);
    setError(null);
  }, []);

  // Build chart data — one row per timeline point, one key per keyword
  const chartData = interestResult?.timelineData.map((point) => {
    const row: Record<string, string | number> = {
      date: point.formattedDate || point.date,
    };
    // The backend returns a single series per request; use the first keyword as the key
    if (interestResult.keywords.length === 1) {
      row[interestResult.keywords[0]] = point.value;
    }
    return row;
  }) ?? [];

  // When multiple keywords are compared the backend returns one series each;
  // we merge them as best we can using the index position.
  const chartKeywords = interestResult?.keywords ?? [];

  const hasResults =
    autocompleteResult !== null ||
    interestResult !== null ||
    relatedResult !== null;

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-3">
        <TrendingUp className="h-7 w-7 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Tendências Google</h1>
          <p className="text-sm text-muted-foreground">
            Pesquise tendências de busca, interesse ao longo do tempo e consultas relacionadas
          </p>
        </div>
      </div>

      {/* Search Bar Section */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <Label htmlFor="trends-keyword" className="sr-only">
                  Palavra-chave
                </Label>
                <Input
                  id="trends-keyword"
                  placeholder="Digite uma palavra-chave..."
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void handleSearch();
                  }}
                />
              </div>

              <div className="flex gap-2">
                <Select value={country} onValueChange={setCountry}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="País" />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={timeframe} onValueChange={setTimeframe}>
                  <SelectTrigger className="w-36">
                    <SelectValue placeholder="Período" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIMEFRAME_OPTIONS.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-2">
              <Button onClick={() => void handleSearch()} disabled={!keyword.trim() || loadingAutocomplete || loadingInterest || loadingRelated}>
                {(loadingAutocomplete || loadingInterest || loadingRelated) ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Search className="h-4 w-4 mr-2" />
                )}
                Pesquisar
              </Button>
              {hasResults && (
                <Button variant="outline" onClick={handleClear}>
                  <XCircle className="h-4 w-4 mr-2" />
                  Limpar
                </Button>
              )}
            </div>

            {/* Active keywords comparison chips */}
            {keywords.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-1">
                <span className="text-sm text-muted-foreground self-center">Comparando:</span>
                {keywords.map((kw, index) => (
                  <Badge
                    key={kw}
                    variant="secondary"
                    className="flex items-center gap-1 pr-1"
                    style={{ borderLeftColor: KEYWORD_COLORS[index % KEYWORD_COLORS.length], borderLeftWidth: 3 }}
                  >
                    {kw}
                    <button
                      type="button"
                      onClick={() => void handleRemoveKeyword(kw)}
                      className="ml-1 rounded hover:bg-muted-foreground/20 p-0.5"
                      aria-label={`Remover ${kw}`}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Error message */}
          {error && (
            <div className="mt-4 flex items-center gap-2 rounded-md bg-destructive/10 text-destructive px-4 py-3 text-sm">
              <XCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Autocomplete Section */}
      {(autocompleteResult || loadingAutocomplete) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Sugestões de Pesquisa</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingAutocomplete ? (
              <div className="flex items-center gap-2 text-muted-foreground py-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Buscando sugestões...</span>
              </div>
            ) : autocompleteResult && autocompleteResult.suggestions.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {autocompleteResult.suggestions.map((suggestion) => {
                  const alreadyAdded = keywords.includes(suggestion);
                  const atLimit = keywords.length >= 5;
                  return (
                    <button
                      key={suggestion}
                      type="button"
                      disabled={alreadyAdded || atLimit}
                      onClick={() => void handleAddKeyword(suggestion)}
                      className={cn(
                        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition-colors",
                        alreadyAdded
                          ? "border-primary/40 bg-primary/10 text-primary cursor-default"
                          : atLimit
                          ? "border-border text-muted-foreground cursor-not-allowed opacity-50"
                          : "border-border bg-background hover:bg-muted hover:border-primary/50 cursor-pointer"
                      )}
                    >
                      {!alreadyAdded && !atLimit && <Plus className="h-3 w-3" />}
                      {suggestion}
                    </button>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma sugestão encontrada.</p>
            )}
            {keywords.length >= 5 && (
              <p className="mt-3 text-xs text-muted-foreground">
                Limite de 5 palavras-chave para comparação atingido.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Interest Over Time Section */}
      {(interestResult || loadingInterest) && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Interesse ao Longo do Tempo</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingInterest ? (
              <div className="flex items-center gap-2 text-muted-foreground py-8">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Carregando dados do gráfico...</span>
              </div>
            ) : interestResult && interestResult.timelineData.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart
                  data={chartData}
                  margin={{ top: 8, right: 24, left: 0, bottom: 8 }}
                >
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="date"
                    tick={{ fontSize: 11 }}
                    interval="preserveStartEnd"
                    className="text-muted-foreground"
                  />
                  <YAxis
                    domain={[0, 100]}
                    tick={{ fontSize: 11 }}
                    className="text-muted-foreground"
                    label={{ value: 'Interesse (0-100)', angle: -90, position: 'insideLeft', fontSize: 11, offset: 10 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                    labelStyle={{ fontWeight: 600, marginBottom: 4 }}
                  />
                  <Legend wrapperStyle={{ fontSize: 12, paddingTop: 12 }} />
                  {chartKeywords.map((kw, index) => (
                    <Line
                      key={kw}
                      type="monotone"
                      dataKey={kw}
                      stroke={KEYWORD_COLORS[index % KEYWORD_COLORS.length]}
                      strokeWidth={2}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-sm text-muted-foreground py-4">
                Nenhum dado de interesse encontrado para o período selecionado.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Related Queries Section */}
      {(relatedResult || loadingRelated) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Top Queries */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Mais Pesquisadas</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingRelated ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8 px-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Carregando...</span>
                </div>
              ) : relatedResult && relatedResult.topQueries.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Consulta</TableHead>
                      <TableHead className="text-right w-20">Valor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relatedResult.topQueries.map((q) => (
                      <TableRow key={q.query}>
                        <TableCell className="text-sm">{q.query}</TableCell>
                        <TableCell className="text-right text-sm font-medium tabular-nums">
                          {q.value}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground py-4 px-6">
                  Nenhuma consulta encontrada.
                </p>
              )}
            </CardContent>
          </Card>

          {/* Rising Queries */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Em Ascensão</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              {loadingRelated ? (
                <div className="flex items-center gap-2 text-muted-foreground py-8 px-6">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Carregando...</span>
                </div>
              ) : relatedResult && relatedResult.risingQueries.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Consulta</TableHead>
                      <TableHead className="text-right w-32">Crescimento</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {relatedResult.risingQueries.map((q) => (
                      <TableRow key={q.query}>
                        <TableCell className="text-sm">{q.query}</TableCell>
                        <TableCell className="text-right text-sm font-medium tabular-nums text-emerald-600 dark:text-emerald-400">
                          {q.formattedValue}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-sm text-muted-foreground py-4 px-6">
                  Nenhuma consulta em ascensão encontrada.
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
